import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Typography,
  Descriptions,
  Space,
  message,
  Row,
  Col,
  Tag,
  Avatar,
  Empty,
  Modal,
  Skeleton,
  Table,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
  CarOutlined,
  PlusOutlined,
  TrophyOutlined,
  MessageOutlined,
  FileTextOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { Customer, Vehicle, Estimate, WorkOrder } from '../types';
import { customerApi, vehicleApi, estimateApi, workOrderApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import VehicleCard from './VehicleCard';
import VehicleForm from './VehicleForm';
import CustomerRewardsTab from './admin/CustomerRewardsTab';
import CustomerCommunicationTab from './admin/CustomerCommunicationTab';

const { Title } = Typography;

// Helper function to get role-based path prefix
const getRolePrefix = (userRole?: string): string => {
  return userRole === 'customer' ? '/portal' :
         userRole === 'technician' ? '/technician' :
         userRole === 'advisor' ? '/advisor' :
         userRole === 'owner' ? '/owner' : '/admin';
};

const CustomerDetail: React.FC = () => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [vehiclesLoading, setVehiclesLoading] = useState(true);
  const [addVehicleModalVisible, setAddVehicleModalVisible] = useState(false);
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [estimatesLoading, setEstimatesLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [workOrdersLoading, setWorkOrdersLoading] = useState(true);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const rolePrefix = getRolePrefix(user?.role);

  useEffect(() => {
    if (id) {
      fetchCustomer(parseInt(id));
      fetchVehicles(parseInt(id));
      fetchEstimates(parseInt(id));
      fetchWorkOrders(parseInt(id));
    }
  }, [id]);

  const fetchCustomer = async (customerId: number) => {
    try {
      setLoading(true);
      const response = await customerApi.getById(customerId);
      setCustomer(response.data);
    } catch (error) {
      message.error('Failed to fetch customer details');
      navigate(`${rolePrefix}/customers`);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async (customerId: number) => {
    try {
      setVehiclesLoading(true);
      const response = await vehicleApi.getByCustomerId(customerId);
      setVehicles(response.data);
    } catch (error) {
      message.error('Failed to fetch vehicles');
    } finally {
      setVehiclesLoading(false);
    }
  };

  // The estimates/work-orders list endpoints are not customer-scoped server-side,
  // so we fetch all and filter by customer_id client-side (matches how the rest of
  // the app reads these lists).
  const fetchEstimates = async (customerId: number) => {
    try {
      setEstimatesLoading(true);
      const response = await estimateApi.getAll();
      setEstimates((response.data || []).filter((e) => e.customer_id === customerId));
    } catch (error) {
      // Non-fatal: customer page still renders without history.
      setEstimates([]);
    } finally {
      setEstimatesLoading(false);
    }
  };

  const fetchWorkOrders = async (customerId: number) => {
    try {
      setWorkOrdersLoading(true);
      const response = await workOrderApi.getAll({ customer_id: customerId });
      setWorkOrders(response.data || []);
    } catch (error) {
      setWorkOrders([]);
    } finally {
      setWorkOrdersLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`${rolePrefix}/customers`);
  };

  const formatDate = (date?: string) => (date ? new Date(date).toLocaleDateString() : '—');
  const formatMoney = (amount?: number) => `$${(amount ?? 0).toFixed(2)}`;

  const estimateStatusColor = (status?: string): string => {
    switch (status) {
      case 'approved': return 'green';
      case 'converted': return 'cyan';
      case 'sent': return 'blue';
      case 'declined': return 'red';
      default: return 'default';
    }
  };

  const workOrderStatusColor = (status?: string): string => {
    switch (status) {
      case 'completed': return 'green';
      case 'in_progress': return 'blue';
      case 'waiting_parts':
      case 'waiting_approval': return 'orange';
      case 'cancelled': return 'red';
      default: return 'default';
    }
  };

  const getContactMethodIcon = (method: string) => {
    switch (method) {
      case 'phone':
        return <PhoneOutlined />;
      case 'email':
        return <MailOutlined />;
      default:
        return <PhoneOutlined />;
    }
  };

  const getCustomerDisplayName = (customer: Customer) => {
    return customer.company_name || `${customer.first_name} ${customer.last_name}`;
  };

  const handleAddVehicle = () => {
    setAddVehicleModalVisible(true);
  };

  const handleAddVehicleSuccess = () => {
    setAddVehicleModalVisible(false);
    fetchVehicles(parseInt(id!));
  };

  if (loading || !customer) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%', overflowX: 'hidden' }}>
        <Card loading={true} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', width: '100%', overflowX: 'hidden' }}>
      <Space direction="vertical" size={24} style={{ width: '100%' }}>
        <Card>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={handleBack}
                style={{ marginBottom: window.innerWidth >= 768 ? 16 : 12 }}
              >
                Back to Customers
              </Button>
              <Row
                justify="space-between"
                align="middle"
                gutter={[16, 16]}
                style={{ marginLeft: 0, marginRight: 0 }}
              >
                <Col xs={24} sm={16} md={18}>
                  <Space align="center" direction={window.innerWidth >= 640 ? 'horizontal' : 'vertical'} style={{ width: '100%' }}>
                    <Avatar
                      size={window.innerWidth >= 768 ? 64 : 48}
                      icon={<UserOutlined />}
                      style={{ backgroundColor: '#1890ff' }}
                    />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <Title
                        level={window.innerWidth >= 768 ? 2 : 3}
                        style={{
                          margin: 0,
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word'
                        }}
                      >
                        {getCustomerDisplayName(customer)}
                      </Title>
                      <Tag
                        icon={getContactMethodIcon(customer.preferred_contact_method || 'phone')}
                        color="blue"
                        style={{ marginTop: 8 }}
                      >
                        {customer.preferred_contact_method || 'phone'}
                      </Tag>
                    </div>
                  </Space>
                </Col>
                <Col xs={24} sm={8} md={6}>
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => navigate(`${rolePrefix}/customers/${customer.id}/edit`)}
                    block={window.innerWidth < 640}
                    size={window.innerWidth >= 768 ? 'middle' : 'large'}
                  >
                    Edit Customer
                  </Button>
                </Col>
              </Row>
            </div>

            {(() => {
              const descCols = window.innerWidth >= 640 ? 2 : 1;
              const fullSpan = descCols;
              return (
                <Descriptions
                  column={descCols}
                  bordered
                  size={window.innerWidth >= 768 ? 'default' : 'small'}
                >
                  <Descriptions.Item label="Name" span={customer.company_name ? 1 : fullSpan}>
                    {customer.first_name} {customer.last_name}
                  </Descriptions.Item>
                  {customer.company_name && (
                    <Descriptions.Item label="Company">
                      {customer.company_name}
                    </Descriptions.Item>
                  )}
                  <Descriptions.Item label="Phone">
                    <Space size="small">
                      <PhoneOutlined />
                      <span style={{ wordBreak: 'break-word' }}>{customer.phone}</span>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    <Space size="small">
                      <MailOutlined />
                      <span style={{ wordBreak: 'break-word' }}>{customer.email}</span>
                    </Space>
                  </Descriptions.Item>
                  <Descriptions.Item label="Address" span={fullSpan}>
                    {[
                      customer.address,
                      customer.city,
                      [customer.state, customer.zip].filter(Boolean).join(' '),
                    ]
                      .filter((part) => part && part.trim())
                      .join(', ') || '—'}
                  </Descriptions.Item>
                  {customer.notes && (
                    <Descriptions.Item label="Notes" span={fullSpan}>
                      {customer.notes}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              );
            })()}
          </Space>
        </Card>

        <Card>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Row
              justify="space-between"
              align="middle"
              gutter={[16, 16]}
              style={{ marginLeft: 0, marginRight: 0 }}
            >
              <Col xs={24} sm={16} md={18}>
                <Title level={window.innerWidth >= 768 ? 3 : 4} style={{ margin: 0 }}>
                  <CarOutlined /> Vehicles ({vehicles.length})
                </Title>
              </Col>
              <Col xs={24} sm={8} md={6}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  size={window.innerWidth >= 768 ? 'large' : 'middle'}
                  onClick={handleAddVehicle}
                  block={window.innerWidth < 640}
                  style={{ height: window.innerWidth >= 768 ? 48 : 40, fontSize: window.innerWidth >= 768 ? 16 : 14 }}
                >
                  Add Vehicle
                </Button>
              </Col>
            </Row>

            {vehiclesLoading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : vehicles.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No vehicles registered for this customer"
                style={{ padding: window.innerWidth >= 768 ? '40px 0' : '20px 0' }}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  size={window.innerWidth >= 768 ? 'large' : 'middle'}
                  onClick={handleAddVehicle}
                  style={{ height: window.innerWidth >= 768 ? 48 : 40, fontSize: window.innerWidth >= 768 ? 16 : 14 }}
                >
                  Add First Vehicle
                </Button>
              </Empty>
            ) : (
              <Row
                gutter={[window.innerWidth >= 768 ? 16 : 12, window.innerWidth >= 768 ? 16 : 12]}
                style={{ marginLeft: 0, marginRight: 0 }}
              >
                {vehicles.map((vehicle) => (
                  <Col xs={24} sm={24} md={12} lg={12} xl={8} xxl={6} key={vehicle.id}>
                    <VehicleCard
                      vehicle={vehicle}
                      onUpdate={() => fetchVehicles(parseInt(id!))}
                    />
                  </Col>
                ))}
              </Row>
            )}
          </Space>
        </Card>

        {/* Rewards Section */}
        <Card>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Title level={window.innerWidth >= 768 ? 3 : 4} style={{ margin: 0 }}>
              <TrophyOutlined style={{ color: '#faad14' }} /> Rewards &amp; Loyalty
            </Title>
            <CustomerRewardsTab customerId={parseInt(id!)} />
          </Space>
        </Card>

        {/* Estimates Section */}
        <Card>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Title level={window.innerWidth >= 768 ? 3 : 4} style={{ margin: 0 }}>
              <FileTextOutlined style={{ color: '#1890ff' }} /> Estimates ({estimates.length})
            </Title>
            {estimatesLoading ? (
              <Skeleton active paragraph={{ rows: 2 }} />
            ) : estimates.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No estimates for this customer"
              />
            ) : (
              <Table<Estimate>
                dataSource={estimates}
                rowKey={(row) => String(row.id)}
                size="small"
                pagination={false}
                scroll={{ x: 'max-content' }}
                onRow={(row) => ({
                  onClick: () => navigate(`${rolePrefix}/estimates/${row.id}`),
                  style: { cursor: 'pointer' },
                })}
                columns={[
                  {
                    title: 'Estimate #',
                    dataIndex: 'estimate_number',
                    key: 'estimate_number',
                    render: (num: string) => <a>{num}</a>,
                  },
                  {
                    title: 'Date',
                    dataIndex: 'created_at',
                    key: 'created_at',
                    render: (d?: string) => formatDate(d),
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    key: 'status',
                    render: (s: string) => (
                      <Tag color={estimateStatusColor(s)}>{(s || 'draft').toUpperCase()}</Tag>
                    ),
                  },
                  {
                    title: 'Total',
                    dataIndex: 'total_amount',
                    key: 'total_amount',
                    align: 'right' as const,
                    render: (t?: number) => formatMoney(t),
                  },
                ]}
              />
            )}
          </Space>
        </Card>

        {/* Work Orders Section */}
        <Card>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Title level={window.innerWidth >= 768 ? 3 : 4} style={{ margin: 0 }}>
              <ToolOutlined style={{ color: '#722ed1' }} /> Work Orders ({workOrders.length})
            </Title>
            {workOrdersLoading ? (
              <Skeleton active paragraph={{ rows: 2 }} />
            ) : workOrders.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No work orders for this customer"
              />
            ) : (
              <Table<WorkOrder>
                dataSource={workOrders}
                rowKey={(row) => String(row.id)}
                size="small"
                pagination={false}
                scroll={{ x: 'max-content' }}
                onRow={(row) => ({
                  onClick: () => navigate(`${rolePrefix}/work-orders/${row.id}`),
                  style: { cursor: 'pointer' },
                })}
                columns={[
                  {
                    title: 'WO #',
                    dataIndex: 'wo_number',
                    key: 'wo_number',
                    render: (num: string) => <a>{num}</a>,
                  },
                  {
                    title: 'Date',
                    dataIndex: 'created_at',
                    key: 'created_at',
                    render: (d?: string) => formatDate(d),
                  },
                  {
                    title: 'Status',
                    dataIndex: 'status',
                    key: 'status',
                    render: (s: string) => (
                      <Tag color={workOrderStatusColor(s)}>
                        {(s || 'new').replace(/_/g, ' ').toUpperCase()}
                      </Tag>
                    ),
                  },
                  {
                    title: 'Total',
                    dataIndex: 'total_amount',
                    key: 'total_amount',
                    align: 'right' as const,
                    render: (t?: number) => formatMoney(t),
                  },
                ]}
              />
            )}
          </Space>
        </Card>

        {/* Communications Section */}
        <Card>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Title level={window.innerWidth >= 768 ? 3 : 4} style={{ margin: 0 }}>
              <MessageOutlined style={{ color: '#1890ff' }} /> Communications History
            </Title>
            <CustomerCommunicationTab
              customerId={parseInt(id!)}
              customerPhone={customer.phone}
              customerEmail={customer.email}
            />
          </Space>
        </Card>

        <Modal
          title={`Add Vehicle for ${getCustomerDisplayName(customer)}`}
          open={addVehicleModalVisible}
          onCancel={() => setAddVehicleModalVisible(false)}
          footer={null}
          width="95%"
          style={{ maxWidth: 900, top: window.innerWidth >= 768 ? 20 : 10 }}
          styles={{ body: { maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' } }}
          destroyOnHidden
        >
          <VehicleForm
            customerId={parseInt(id!)}
            onSuccess={handleAddVehicleSuccess}
            onCancel={() => setAddVehicleModalVisible(false)}
            modal
          />
        </Modal>
      </Space>
    </div>
  );
};

export default CustomerDetail;